import { css, type SerializedStyles } from '@emotion/react'
import { type Theme } from '@mui/material/styles'
import { rem, size } from 'polished'

import { RADIUS_1 } from '@Styles/constants/radius'

import { type DateTimeButtonSize } from '../atoms/DateTimeButton.types'

const BUTTON_HEIGHT = rem(56)
const SMALL_BUTTON_HEIGHT = rem(48)
const SEPARATOR_WIDTH = rem(2)

type DateSearchCriteriaMode = 'inward' | 'outward' | 'singleDate'

const getInputBorderRadiusByMode = (mode: DateSearchCriteriaMode): string =>
  ({
    inward: `0 ${RADIUS_1} ${RADIUS_1} 0`,
    outward: `${RADIUS_1} 0 0 ${RADIUS_1}`,
    singleDate: RADIUS_1,
  })[mode]

export const datesWrapper = (isOutwardOnly: boolean, buttonSize?: DateTimeButtonSize): SerializedStyles =>
  css({
    cursor: 'pointer',
    display: 'flex',
    height: buttonSize === 'small' ? SMALL_BUTTON_HEIGHT : BUTTON_HEIGHT,
    maxWidth: isOutwardOnly ? '50%' : '100%',
    minWidth: isOutwardOnly ? rem(217) : undefined,
  })

export const inwardInput = css({
  marginLeft: SEPARATOR_WIDTH,
  maxWidth: `calc(100% - ${SEPARATOR_WIDTH})`,
})

export const startIcon = (theme: Theme): SerializedStyles =>
  css({
    ...size(16),
    fill: 'currentcolor',
    marginRight: theme.spacing(0),
  })

export const inputDate = (
  theme: Theme,
  mode: DateSearchCriteriaMode,
  disabled: boolean | undefined,
  isElevated: boolean | undefined,
): SerializedStyles =>
  css(
    !disabled && {
      ':hover': {
        backgroundColor: theme.customPalette.onElements.selected,
      },
    },
    {
      '&:focus-visible': {
        outline: 'none',
      },
      '&:focus-visible::before': {
        border: `2px solid ${theme.palette.primary.main}`,
        borderRadius: getInputBorderRadiusByMode(mode),
        bottom: 0,
        content: '""',
        display: 'block',
        left: 0,
        position: 'absolute',
        right: 0,
        top: 0,
      },
      backgroundColor: isElevated ? theme.customPalette.background.default : theme.customPalette.background.paper,
      border: `2px solid transparent`,
      borderRadius: getInputBorderRadiusByMode(mode),
      color: disabled ? theme.palette.text.disabled : theme.palette.primary.main,
      cursor: disabled ? 'default' : 'pointer',
      padding: theme.spacing(1.5, 2.25, 1.5, 2),
    },
  )

export const buttonWrapper = (theme: Theme, isWrapped: boolean): SerializedStyles =>
  css({
    flexBasis: '50%',
    flexGrow: 1,
    marginTop: isWrapped ? theme.spacing(1) : 0,
    position: 'relative',
  })

export const disabledLabel = (theme: Theme): SerializedStyles =>
  css({
    color: theme.palette.text.disabled,
  })

export const label = (theme: Theme, isDisabled: boolean): SerializedStyles =>
  css({
    color: isDisabled ? theme.palette.text.disabled : theme.palette.text.secondary,
  })

export const button = (
  theme: Theme,
  isFilled: boolean,
  disabled: boolean,
  buttonSize?: DateTimeButtonSize,
): SerializedStyles =>
  css({
    ':focus': {
      outlineColor: theme.palette.primary.main,
    },
    alignItems: 'center',
    backgroundColor: 'transparent',
    border: 0,
    color: isFilled ? theme.palette.text[disabled ? 'disabled' : 'secondary'] : theme.palette.primary.main,
    display: 'flex',
    height: buttonSize === 'small' ? SMALL_BUTTON_HEIGHT : BUTTON_HEIGHT,
    padding: 0,
    textOverflow: 'ellipsis',
    width: '100%',
  })

export const value = (theme: Theme, disabled: boolean): SerializedStyles =>
  css({
    color: theme.palette.text[disabled ? 'disabled' : 'primary'],
    marginRight: theme.spacing(0),
  })

export const removeButton = (theme: Theme): SerializedStyles =>
  css({
    position: 'absolute',
    right: theme.spacing(2),
    top: '50%',
    transform: 'translateY(-50%)',
  })

export const loadingIcon = css({
  flexShrink: 0,
  padding: 0,
})
